import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { resolveServerRoot } from "../helpers/api_origin";
import {
  getSuperAdminLastTenant,
  SUPER_ADMIN_LAST_TENANT_EVENT,
} from "../helpers/super_admin_last_tenant";
import { getStoredSuperAdminAuth } from "../helpers/super_admin_auth_storage";
import { getBlogSummary } from "../services/superAdmin/adminBlogService";
import { getAdminSupportConversationSummary } from "../services/superAdmin/adminSupportConversationService";
import { getAdminSupportSummary } from "../services/superAdmin/adminSupportTicketService";

const AUDIT_MENU_ID = "admin-audit";
const MONITORING_MENU_ID = "admin-monitoring";
const SUPPORT_QUEUE_POLL_MS = 30_000;

const pathOpensMenu = (pathname: string): string | null => {
  if (pathname.startsWith("/super-admin/activity-logs")) {
    return AUDIT_MENU_ID;
  }

  if (
    pathname.startsWith("/super-admin/webhook-logs") ||
    pathname.startsWith("/super-admin/support/") ||
    pathname.startsWith("/super-admin/email-suppressions")
  ) {
    return MONITORING_MENU_ID;
  }

  return null;
};

const SuperAdminLayoutMenuData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [pendingBlogCount, setPendingBlogCount] = useState(0);
  const [unassignedSupportCount, setUnassignedSupportCount] = useState(0);
  const [queuedSupportChatCount, setQueuedSupportChatCount] = useState(0);
  const [lastTenant, setLastTenant] = useState(() => getSuperAdminLastTenant());
  const [activeMenuId, setActiveMenuId] = useState<string | null>(() =>
    pathOpensMenu(location.pathname)
  );

  useEffect(() => {
    const fromPath = pathOpensMenu(location.pathname);
    if (fromPath) {
      setActiveMenuId(fromPath);
    }
  }, [location.pathname]);

  useEffect(() => {
    const syncLastTenant = () => setLastTenant(getSuperAdminLastTenant());

    syncLastTenant();
    window.addEventListener(SUPER_ADMIN_LAST_TENANT_EVENT, syncLastTenant);
    window.addEventListener("focus", syncLastTenant);

    return () => {
      window.removeEventListener(SUPER_ADMIN_LAST_TENANT_EVENT, syncLastTenant);
      window.removeEventListener("focus", syncLastTenant);
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [blogResponse, supportResponse, chatSummaryResponse] =
          await Promise.all([
            getBlogSummary(),
            getAdminSupportSummary(),
            getAdminSupportConversationSummary(),
          ]);

        if (cancelled) {
          return;
        }

        setPendingBlogCount(blogResponse.data.pending_review_count || 0);
        setUnassignedSupportCount(supportResponse.data.unassigned_open || 0);
        setQueuedSupportChatCount(chatSummaryResponse.data?.queued_human || 0);
      } catch {
        // menu badges are best-effort
      }
    };

    void load();
    const timer = window.setInterval(() => {
      void load();
    }, SUPPORT_QUEUE_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [location.pathname]);

  const handleHorizonClick = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const auth = getStoredSuperAdminAuth();

      if (!auth?.token) {
        toast.error(t("admin.horizon.sessionMissing"));
        return;
      }

      try {
        const response = await fetch(
          `${resolveServerRoot()}/super-admin/horizon/session`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              Accept: "application/json",
            },
          }
        );

        const body = await response.json().catch(() => null);
        const launchPath = body?.data?.launch_path;

        if (!response.ok || typeof launchPath !== "string") {
          toast.error(body?.message || t("admin.horizon.openFailed"));
          return;
        }

        window.open(
          `${resolveServerRoot()}${launchPath}`,
          "_blank",
          "noopener,noreferrer"
        );
      } catch {
        toast.error(t("admin.horizon.openFailed"));
      }
    },
    [t]
  );

  const makeSubmenuProps = useCallback(
    (menuId: string, link: string) => ({
      link,
      stateVariables: activeMenuId === menuId,
      click: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setActiveMenuId((current) => (current === menuId ? null : menuId));
      },
    }),
    [activeMenuId]
  );

  const menuItems: any[] = useMemo(
    () => [
      {
        label: "menu.admin.platform",
        isHeader: true,
      },
      {
        id: "admin-dashboard",
        label: "menu.admin.dashboard",
        icon: "ri-dashboard-2-line",
        link: "/super-admin/dashboard",
      },
      {
        id: "admin-plans",
        label: "menu.admin.plans",
        icon: "ri-price-tag-3-line",
        link: "/super-admin/plans",
      },
      {
        id: "admin-promotion-coupons",
        label: "menu.admin.promotionCoupons",
        icon: "ri-coupon-3-line",
        link: "/super-admin/promotion-coupons",
      },
      {
        id: "admin-blog",
        label: "menu.admin.blog",
        icon: "ri-article-line",
        link: "/super-admin/blog",
        ...(pendingBlogCount > 0
          ? {
              badgeName: String(pendingBlogCount),
              badgeColor: "warning",
            }
          : {}),
      },
      {
        id: "admin-health-record-presets",
        label: "menu.admin.healthRecordPresets",
        icon: "ri-file-list-3-line",
        link: "/super-admin/health-record-presets",
      },
      {
        id: "admin-tenants",
        label: "menu.admin.tenants",
        icon: "ri-building-4-line",
        link: "/super-admin/tenants",
      },
      {
        id: "admin-accounts",
        label: "menu.admin.accounts",
        icon: "ri-user-shared-line",
        link: "/super-admin/accounts",
      },
      ...(lastTenant
        ? [
            {
              id: "admin-last-tenant",
              label: "menu.admin.lastTenantNamed",
              labelParams: { name: lastTenant.name },
              icon: "ri-history-line",
              link: `/super-admin/tenants?tenant_id=${lastTenant.id}`,
            },
          ]
        : []),
      {
        id: "admin-trial-queue",
        label: "menu.admin.trialQueue",
        icon: "ri-rocket-2-line",
        link: "/super-admin/trial-queue",
      },
      {
        id: "admin-cs-nurture",
        label: "menu.admin.csNurture",
        icon: "ri-mail-send-line",
        link: "/super-admin/cs-nurture",
      },
      {
        id: "admin-concierge",
        label: "menu.admin.concierge",
        icon: "ri-calendar-schedule-line",
        link: "/super-admin/concierge",
      },
      {
        id: AUDIT_MENU_ID,
        label: "menu.admin.audit",
        icon: "ri-shield-keyhole-line",
        ...makeSubmenuProps(AUDIT_MENU_ID, "/super-admin/activity-logs"),
        subItems: [
          {
            id: "admin-activity-logs",
            label: "menu.admin.activityLogs",
            link: "/super-admin/activity-logs",
            parentId: AUDIT_MENU_ID,
          },
        ],
      },
      {
        id: MONITORING_MENU_ID,
        label: "menu.admin.monitoring",
        icon: "ri-pulse-line",
        ...makeSubmenuProps(MONITORING_MENU_ID, "/super-admin/support/tickets"),
        ...(unassignedSupportCount + queuedSupportChatCount > 0
          ? {
              badgeName: String(
                unassignedSupportCount + queuedSupportChatCount
              ),
              badgeColor: "danger",
            }
          : {}),
        subItems: [
          {
            id: "admin-horizon",
            label: "menu.admin.horizon",
            externalLink: true,
            link: "#",
            target: "_blank",
            onClick: handleHorizonClick,
            parentId: MONITORING_MENU_ID,
          },
          {
            id: "admin-webhook-logs",
            label: "menu.admin.webhookLogs",
            link: "/super-admin/webhook-logs",
            parentId: MONITORING_MENU_ID,
          },
          {
            id: "admin-support-conversations",
            label: "menu.admin.supportConversations",
            link: "/super-admin/support/conversations",
            parentId: MONITORING_MENU_ID,
            ...(queuedSupportChatCount > 0
              ? {
                  badgeName: String(queuedSupportChatCount),
                  badgeColor: "danger",
                }
              : {}),
          },
          {
            id: "admin-support-tickets",
            label: "menu.admin.supportTickets",
            link: "/super-admin/support/tickets",
            parentId: MONITORING_MENU_ID,
            ...(unassignedSupportCount > 0
              ? {
                  badgeName: String(unassignedSupportCount),
                  badgeColor: "danger",
                }
              : {}),
          },
          {
            id: "admin-email-suppressions",
            label: "menu.admin.emailSuppressions",
            link: "/super-admin/email-suppressions",
            parentId: MONITORING_MENU_ID,
          },
        ],
      },
    ],
    [
      handleHorizonClick,
      lastTenant,
      makeSubmenuProps,
      pendingBlogCount,
      queuedSupportChatCount,
      unassignedSupportCount,
    ]
  );

  return <React.Fragment>{menuItems}</React.Fragment>;
};

export default SuperAdminLayoutMenuData;
